import { get, post, handleError } from '@/lib/http-adapter';
import type { MetaAttribution } from '@/services/tracking';
import type { AcceptedTerms } from '@/services/terms';
import type { EntityImage } from '@/lib/entity-image';
import type { FaqItem } from '@/components/faq-accordion';

export type TopupSourceType = 'PACK' | 'SUBSCRIPTION';

// Static singleton top-up content for the details page (GET /public/topups).
export interface TopupContent {
  topup_id: string;
  slug: string;
  name: string;
  short_description: string | null;
  description: string | null;
  image: string | null;
  images?: EntityImage[];
  currency: string;
  same_price_for_all: boolean;
  price_for_packs: number | null;
  price_for_subscriptions: number | null;
  max_quantity_per_purchase: number;
  expiry_policy: 'EXPIRE_WITH_PLAN' | 'NEVER';
  faqs: FaqItem[];
  // Plans the top-up is linked to; which arrays are non-empty defines its scope.
  topup_class_packs?: { class_pack_id: string }[];
  topup_subscriptions?: { subscription_id: string }[];
}

export const getTopup = async (
  slug?: string
): Promise<TopupContent | null> => {
  try {
    const path = slug
      ? `public/topups?slug=${encodeURIComponent(slug)}`
      : 'public/topups';
    const response = await get<{ topup: TopupContent | null }>(path);
    return response?.topup ?? null;
  } catch (error) {
    handleError(error);
    return null;
  }
};

export type TopupIneligibilityReason =
  | 'NO_TOPUP'
  | 'NO_LINKED_PLANS'
  | 'NO_PURCHASE'
  | 'NO_ELIGIBLE_PLAN'
  | 'CREDITS_REMAINING'
  | 'NO_PRICE'
  | 'PER_PLAN_LIMIT_REACHED';

export interface TopupEligibility {
  eligible: boolean;
  reason: TopupIneligibilityReason | null;
  topup: {
    topup_id: string;
    name: string;
    image: string | null;
    currency: string;
    max_quantity_per_purchase: number;
  } | null;
  price: number | null;
  currency: string | null;
  source_type: TopupSourceType | null;
  source_plan_name: string | null;
  max_quantity: number | null;
  // Expiry the purchased credits will inherit: a date, or null when they never expire.
  credits_expire_at: string | null;
}

export const getTopupEligibility = async (
  slug?: string
): Promise<TopupEligibility | undefined> => {
  try {
    const path = slug
      ? `public/topups/eligibility?slug=${encodeURIComponent(slug)}`
      : 'public/topups/eligibility';
    return await get<TopupEligibility>(path);
  } catch (error) {
    handleError(error);
  }
};

interface CreateTopupOrderPayload {
  quantity: number;
  user_id?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  meta_attribution?: MetaAttribution;
  // Direct-link credential needed to buy a PRIVATE top-up.
  slug?: string;
  accepted_terms?: AcceptedTerms[];
}

export const createTopupOrder = async (
  payload: CreateTopupOrderPayload
): Promise<{ checkout_url: string | null; order_id: string } | undefined> => {
  try {
    return await post('/public/orders/topup', payload);
  } catch (error) {
    handleError(error);
  }
};
